import type { FC, ReactNode } from 'react'
import type { CartLine, ProductSummary, QuotedLine } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import {
    CART_STORAGE_KEY,
    MAX_CART_LINES,
    clampQuantity,
    lineKey
} from '@/lib/cart'

interface CartContextValue {
    lines: Array<CartLine>
    ready: boolean
    itemCount: number
    subtotal: number
    isOpen: boolean
    setOpen: (open: boolean) => void
    addItem: (product: ProductSummary, variant: string | null, quantity: number) => void
    setQuantity: (key: string, quantity: number) => void
    removeItem: (key: string) => void
    clear: () => void
    syncLines: (quoted: Array<QuotedLine>) => void
}

interface CartProviderProps {
    children: ReactNode
}

const CartContext = createContext<CartContextValue | null>(null)

const isCartLine = (value: unknown): value is CartLine => {
    if (!value || typeof value !== 'object') return false

    const line = value as Record<string, unknown>

    return typeof line.key === 'string'
        && typeof line.product_id === 'string'
        && typeof line.slug === 'string'
        && typeof line.name === 'string'
        && typeof line.price === 'number'
        && typeof line.quantity === 'number'
}

const readStoredCart = (): Array<CartLine> => {
    try {
        const raw = window.localStorage.getItem(CART_STORAGE_KEY)

        if (!raw) return []

        const parsed: unknown = JSON.parse(raw)

        if (!Array.isArray(parsed)) return []

        return parsed
            .filter(isCartLine)
            .slice(0, MAX_CART_LINES)
            .map(line => ({
                ...line,
                image: typeof line.image === 'string' ? line.image : null,
                image_alt: typeof line.image_alt === 'string' ? line.image_alt : null,
                category_slug: typeof line.category_slug === 'string' ? line.category_slug : null,
                variant: typeof line.variant === 'string' ? line.variant : null,
                quantity: clampQuantity(line.quantity)
            }))
    }
    catch {
        return []
    }
}

const writeStoredCart = (lines: Array<CartLine>): void => {
    try {
        window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines))
    }
    catch {
        // Storage can be full or blocked. The cart still works for this visit.
    }
}

/*
 * The cart lives in the browser (it is safe to lose). Prices are always
 * re-checked on the server before an order is saved.
 */
export const CartProvider: FC<CartProviderProps> = ({ children }): ReactNode => {
    const [lines, setLines] = useState<Array<CartLine>>([])
    const [ready, setReady] = useState(false)
    const [isOpen, setOpen] = useState(false)

    useEffect(() => {
        setLines(readStoredCart())
        setReady(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key === CART_STORAGE_KEY) setLines(readStoredCart())
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const update = useCallback((recipe: (current: Array<CartLine>) => Array<CartLine>) => {
        setLines(current => {
            const next = recipe(current)

            if (next !== current) writeStoredCart(next)

            return next
        })
    }, [])

    const addItem = useCallback((product: ProductSummary, variant: string | null, quantity: number) => {
        const key = lineKey(product.id, variant)

        update(current => {
            const existing = current.find(line => line.key === key)

            if (existing) {
                return current.map(line => line.key === key
                    ? { ...line, quantity: clampQuantity(line.quantity + quantity) }
                    : line)
            }

            const line: CartLine = {
                key,
                product_id: product.id,
                slug: product.slug,
                name: product.name,
                price: product.price,
                image: product.image,
                image_alt: product.image_alt,
                category_slug: product.category_slug,
                variant,
                quantity: clampQuantity(quantity)
            }

            return [...current, line].slice(-MAX_CART_LINES)
        })

        setOpen(true)
    }, [update])

    const setQuantity = useCallback((key: string, quantity: number) => {
        update(current => current.map(line => line.key === key
            ? { ...line, quantity: clampQuantity(quantity) }
            : line))
    }, [update])

    const removeItem = useCallback((key: string) => {
        update(current => current.filter(line => line.key !== key))
    }, [update])

    const clear = useCallback(() => {
        update(() => [])
    }, [update])

    const syncLines = useCallback((quoted: Array<QuotedLine>) => {
        const fresh = new Map(quoted.map(line => [line.key, line]))

        update(current => {
            let changed = false

            const next = current.map(line => {
                const match = fresh.get(line.key)

                if (!match) return line

                const same = match.name === line.name
                    && match.price === line.price
                    && match.image === line.image
                    && match.image_alt === line.image_alt
                    && match.slug === line.slug
                    && match.category_slug === line.category_slug

                if (same) return line

                changed = true

                return {
                    ...line,
                    name: match.name,
                    price: match.price,
                    image: match.image,
                    image_alt: match.image_alt,
                    slug: match.slug,
                    category_slug: match.category_slug
                }
            })

            return changed ? next : current
        })
    }, [update])

    const value = useMemo<CartContextValue>(() => ({
        lines,
        ready,
        itemCount: lines.reduce((total, line) => total + line.quantity, 0),
        subtotal: lines.reduce((total, line) => total + line.price * line.quantity, 0),
        isOpen,
        setOpen,
        addItem,
        setQuantity,
        removeItem,
        clear,
        syncLines
    }), [lines, ready, isOpen, addItem, setQuantity, removeItem, clear, syncLines])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider')

    return context
}
